import { useAppDispatch, useAppSelector } from "../../app";
import type { AppDispatch } from "../../app/store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OptionType } from "../interfaces/classifier";
import { fetchClassifierOptionsSearch } from "../utils/classifier";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
  changeEditing,
  changeHasBeenEdited,
} from "../../app/store/appSlice";
import {
  CategorizationWithConfig,
  CategorizationAnnotation,
} from "../../configMap/interfaces/category";
import selectCategorizationAnnotationId from "../selectors/selectCategorizationAnnotationId";
import {
  removeCategorizationAnnotation,
  removeIncompleteCategorization,
  updateCategorizationAnnotation,
} from "../store/metadataSlice";
import useEntityAnnotations from "./useEntityAnnotations";
import { Status } from "../../common/status/status";

import { findOriginalIndex } from "../../documentTypes/utils/synchronizeAnnotationHelper";
import useCategorizationEvents from "./useCategorizationEvents";
import useParentCategorization from "./useParentCategorization";
import { buildSearchQuery } from "../../common/utilities/requests";
import { useTranslation } from "react-i18next";

const useFixedCategorization = (
  categorizationWithConfig: CategorizationWithConfig,
  index: number,
  groupBlockId?: string,
) => {
  const { categorization, required } = categorizationWithConfig;
  const { id, search } = categorization;

  const dispatch = useAppDispatch();

  const document = useAppSelector(
    (state) => state.documentReducer.activeDocument,
  );
  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );

  const annotations = useAppSelector(
    (state) => state.annotationReducer.tableControlAnnotations,
  );

  const categoryAnnotation = useAppSelector((state) =>
    selectCategorizationAnnotationId(state, {
      documentId: document?.id,
      categorizationId: id,
      groupBlockId: groupBlockId,
      index: index,
    }),
  );

  const { incompleteCategorizations } = useAppSelector(
    (state) => state.metadataReducer,
  );

  const normalizeEntityAnnotations = useEntityAnnotations();

  const {
    i18n: { language },
  } = useTranslation();

  const { user } = useAppSelector((state) => state.userReducer);

  const [allOptions, setAllOptions] = useState<Array<OptionType>>([]);
  const [filteredOptions, setFilteredOptions] = useState<Array<OptionType>>([]);
  const [emptySearchResult, setEmptySearchResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPrefetched, setIsPrefetched] = useState(false);

  const tabId = useMemo(() => {
    if (groupBlockId) {
      return `categorization-${id}-${groupBlockId}-${index}`;
    }
    return `categorization-${id}-${index}`;
  }, [id, index, groupBlockId]);

  const isLoadingValue = useMemo(() => {
    return categoryAnnotation?.isLoading ?? false;
  }, [categoryAnnotation]);

  const selectedCategory = useMemo(() => {
    if (!categoryAnnotation || isLoadingValue) {
      return null;
    }

    return {
      value: categoryAnnotation.value,
      visibleValue: categoryAnnotation.visibleValue,
    };
  }, [categoryAnnotation, isLoadingValue]);

  const { callEvents } = useCategorizationEvents();

  const { isCategorizationDisabledByParent, disabledParentTooltip } =
    useParentCategorization(
      categoryAnnotation,
      categorization,
      document?.id,
      groupBlockId,
      index,
      activeDocumentSet?.input?.attribute,
    );

  const prefetchOptions = useCallback(
    // The deps are deliberately narrower than the compiler can infer: it widens
    // `user?.organization?.id` / `activeDocumentSet?.input` to the whole `user` /
    // `activeDocumentSet` object. Widening them would give this callback a new
    // identity whenever those Redux objects are re-created with unchanged ids,
    // and it is consumed by the prefetch effect below — so each such re-creation
    // would fire another HTTP request. The narrow deps are the correct ones.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- narrow optional-chain deps are intentional; widening causes redundant fetches
    async (signal: AbortSignal) => {
      if (!search?.endpoint) {
        return;
      }

      try {
        setLoading(true);

        // Build search query for fetching all options (empty search value)
        const input_ids = activeDocumentSet?.input
          ? [activeDocumentSet.input.id]
          : undefined;

        const searchQuery = buildSearchQuery(
          search,
          "", // Empty search to get all options
          null,
          language,
          input_ids,
          undefined,
          user?.organization?.id ? [user.organization.id] : undefined,
        );

        const { data } = await fetchClassifierOptionsSearch(
          search.endpoint,
          searchQuery,
        );

        setAllOptions(data);
        setFilteredOptions(data);
        setIsPrefetched(true);

        if (data.length === 0) {
          setEmptySearchResult(true);
        }
      } catch (e) {
        if (signal.aborted) {
          return;
        }
        console.error("FIXED categorization: Prefetch failed for", id, e);
      } finally {
        setLoading(false);
      }
    },
    [id, search, activeDocumentSet?.input, language, user?.organization?.id],
  );

  // Local filtering for FIXED categorizations
  const handleSearch = useCallback(
    (value: string) => {
      if (!isPrefetched) {
        console.log(
          "FIXED categorization: Not prefetched yet, skipping search",
        );
        return;
      }

      if (!value.trim()) {
        setFilteredOptions(allOptions);
        setEmptySearchResult(false);
        return;
      }

      // Filter locally from prefetched options
      const filtered = allOptions.filter(
        (option) =>
          option.visibleValue?.toLowerCase().includes(value.toLowerCase()) ||
          option.value?.toLowerCase().includes(value.toLowerCase()),
      );

      setFilteredOptions(filtered);
      setEmptySearchResult(filtered.length === 0);

      console.log(
        "FIXED categorization: Local filter applied, found",
        filtered.length,
        "options",
      );
    },
    [allOptions, isPrefetched],
  );

  const removeIncompleteCategorizationInRedux = useCallback(
    (categorizationId: string, index: number, groupBlockId?: string) => {
      dispatch(
        removeIncompleteCategorization({
          categorizationId,
          groupBlockId,
          index,
        }),
      );
    },
    [dispatch],
  );

  const updateCategorizationAnnotationAsync =
    (payload: CategorizationAnnotation) => (dispatch: AppDispatch) => {
      return new Promise<void>((resolve) => {
        dispatch(updateCategorizationAnnotation(payload));
        resolve();
      });
    };

  const handleChange = useCallback(
    async (key: string, callback: () => void) => {
      if (!document?.id) {
        return;
      }

      const found = filteredOptions.find((o) => o.key === key);

      dispatch(changeHasBeenEdited(true));
      if (found) {
        const originalIndex = findOriginalIndex(
          annotations,
          [],
          groupBlockId,
          index,
        );

        await dispatch(
          updateCategorizationAnnotationAsync({
            documentId: document.id,
            categorizationId: id,
            externalDataId: found.key,
            groupBlockId,
            index,
            originalIndex,
            isByUser: true,
            visibleValue: found.visibleValue,
            value: found.value,
          }),
        );

        removeIncompleteCategorizationInRedux(id, index, groupBlockId);

        normalizeEntityAnnotations(
          id,
          found.value,
          index,
          !!groupBlockId,
        ).catch(() => undefined);

        callback();
        return;
      }

      dispatch(
        removeCategorizationAnnotation({
          documentId: document.id,
          categorizationId: id,
          groupBlockId,
          index,
        }),
      );
      callback();
    },
    [
      document?.id,
      id,
      groupBlockId,
      index,
      dispatch,
      normalizeEntityAnnotations,
      removeIncompleteCategorizationInRedux,
      annotations,
      filteredOptions,
    ],
  );

  const handleFocus = useCallback(() => {
    dispatch(changeEditing(tabId));
    dispatch(changeActiveEntity(undefined));
    dispatch(changeAnnotating(false));
    dispatch(changeActiveButton({ id: tabId }));
  }, [dispatch, tabId]);

  const handleBlur = useCallback(() => {
    dispatch(changeEditing(null));
    dispatch(changeActiveButton(undefined));
  }, [dispatch]);

  // For FIXED categorizations, we don't need server-side filtering
  const handleFilter = useCallback(() => {
    return true; // Show all options from handleSearch
  }, []);

  const status = useMemo((): "" | "warning" | "error" => {
    if (
      activeDocumentSet?.status === Status.ExportFailed &&
      !selectedCategory?.value &&
      required
    ) {
      return "error";
    }
    return "";
  }, [activeDocumentSet?.status, selectedCategory, required]);

  const isIncomplete = useMemo(() => {
    const found = incompleteCategorizations.find((bc) => {
      return (
        bc.categorizationId === categorization.id &&
        bc.index === index &&
        bc.groupBlockId === groupBlockId
      );
    });

    return !!found;
  }, [incompleteCategorizations, categorization, index, groupBlockId]);

  // Prefetch options on mount
  useEffect(() => {
    if (!search?.endpoint) {
      return;
    }

    const controller = new AbortController();
    // prefetchOptions is async and only sets state after awaiting the request;
    // the rule cannot see through the async boundary.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- setState happens after an await, not synchronously in the effect body
    prefetchOptions(controller.signal);

    return () => {
      controller.abort();
    };
  }, [prefetchOptions, search?.endpoint]);

  return {
    tabId,
    selectedCategory,
    categorizationOptions: filteredOptions,
    handleChange,
    handleFocus,
    handleBlur,
    handleSearch,
    handleFilter,
    status,
    isCategorizationDisabledByParent,
    disabledParentTooltip,
    isIncomplete,
    callEvents,
    isCategorizationLoading: false,
    emptySearchResult,
    loading,
    isLoadingValue,
    isPrefetched,
  };
};

export default useFixedCategorization;
